/**
 * @file AboutAbaCases.jsx
 * @description Standalone SEO/brand page at /about-aba-cases — explains what
 *   ABAcases is, who it's for, and how the case finder + free pay tracker
 *   work. Honest about pre-launch scale (no volume claims, no fake reviews).
 *   Brand-authority framing only; not a comparison page. Rendered under
 *   TopNav (guest nav), so it has no brand header of its own — mirrors the
 *   structure of CityRolePage.jsx.
 * @module components/AboutAbaCases
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  MapPinIcon, DollarIcon, CalendarIcon, ShieldIcon, BriefcaseIcon,
  UsersIcon, BellIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import { setSignupIntent } from "../lib/signupIntent";
import { useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import AgencyPricingNote from "./AgencyPricingNote";
import { useAgencySignup } from "./AgencyFinalCta";
import ContactModal from "./ContactModal";
import { FounderCard } from "./AgencyVisuals";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

export default function AboutAbaCases({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const isEs = String(i18n.resolvedLanguage || i18n.language || "").toLowerCase().startsWith("es");
  const [contactOpen, setContactOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // The SEO surface (title, description, canonical, JSON-LD) stays English like the other public pages;
  // only the visible page copy follows the language switcher.
  usePageSeo({
    title: "About ABAcases | ABA Case Finder & Pay Tracker",
    description: "Learn about ABAcases, the ABA case finder and free pay tracker built for RBTs, BCBAs, BCaBAs, BTs, ABA therapists, and agencies.",
    canonical: "/about-aba-cases",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-about-aba-cases", [
    { name: "Home", url: "/" },
    { name: "About ABAcases", url: "/about-aba-cases" },
  ]);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as CityRolePage.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  const { label: agencyCtaLabel, go: goAgencySignup } = useAgencySignup({ source: "about_aba_cases", pagePath: "/about-aba-cases/", onNavigate });
  const goJoin = () => { setSignupIntent("alerts"); onNavigate("auth"); };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div lang={isEs ? "es" : "en"} style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t("aboutAbaCases.crumbHome"), onClick: () => onNavigate("home") },
            { label: t("aboutAbaCases.crumbAbout") },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t("aboutAbaCases.badge")}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t("aboutAbaCases.h1")}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t("aboutAbaCases.heroSub")}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t("aboutAbaCases.heroBefore")}{" "}
            {/* Direct href="/" — seoHref("home") would resolve to the un-prerendered
                "/home" view-alias, not the canonical, prerendered homepage. */}
            <a href="/" onClick={(e) => { e.preventDefault(); onNavigate("home"); }} style={{ color: "#fff", fontWeight: 700, borderBottom: "1px solid rgba(255,255,255,0.4)", textDecoration: "none" }}>abacases.com</a>
            {" "}{t("aboutAbaCases.heroAfter")}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: T.shadowIndigo }}>
              <BellIcon size={16} color="#fff" /> {t("aboutAbaCases.joinFree")}
            </button>
          </div>
        </div>
      </header>

      {/* ── 1. What is ABAcases? ─────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.whatTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.whatP1")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t("aboutAbaCases.whatP2")}
          </p>
        </div>
      </section>

      {/* ── 2. Who ABAcases is for ───────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.forTitle")}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16 }}>
            {[
              { Icon: UsersIcon, title: t("aboutAbaCases.forRbtTitle"), body: t("aboutAbaCases.forRbtBody") },
              { Icon: ShieldIcon, title: t("aboutAbaCases.forBcbaTitle"), body: t("aboutAbaCases.forBcbaBody") },
              { Icon: UsersIcon, title: t("aboutAbaCases.forBroadTitle"), body: t("aboutAbaCases.forBroadBody") },
              { Icon: BriefcaseIcon, title: t("aboutAbaCases.forAgencyTitle"), body: t("aboutAbaCases.forAgencyBody") },
            ].map((c) => (
              <div key={c.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{c.body}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginTop: 18 }}>{t("aboutAbaCases.forFamiliesNote")}</p>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginTop: 10 }}>
            {t("aboutAbaCases.forNewBefore")}{" "}
            <SeoLink to="how-to-become-an-rbt" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.forNewLinkRbt")}</SeoLink>{" "}
            {t("aboutAbaCases.forNewMid")}{" "}
            <SeoLink to="rbt-vs-bt-vs-bcba-vs-bcaba" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.forNewLinkRoles")}</SeoLink>.
          </p>
        </div>
      </section>

      {/* ── 3. How ABA case matching works ────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.matchTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.matchP1Before")}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.matchP1Link")}</SeoLink>{" "}
            {t("aboutAbaCases.matchP1After")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t("aboutAbaCases.matchP2")}
          </p>
          <p style={{ fontSize: 15.5, color: NAVY, fontWeight: 600, lineHeight: 1.75, marginTop: 14 }}>
            {t("aboutAbaCases.verifyBoth")}
          </p>
        </div>
      </section>

      {/* ── 4. Why pay, schedule, setting, and commute matter ─── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 22px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.whyTitle")}
          </h2>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 18 }}>
            {[
              { Icon: DollarIcon, title: t("aboutAbaCases.whyPayTitle"), body: t("aboutAbaCases.whyPayBody") },
              { Icon: CalendarIcon, title: t("aboutAbaCases.whyScheduleTitle"), body: t("aboutAbaCases.whyScheduleBody") },
              { Icon: MapPinIcon, title: t("aboutAbaCases.whyLocationTitle"), body: t("aboutAbaCases.whyLocationBody") },
              { Icon: ShieldIcon, title: t("aboutAbaCases.whyCredentialTitle"), body: t("aboutAbaCases.whyCredentialBody") },
            ].map((c) => (
              <div key={c.title} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "18px 20px" }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={18} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 14.5, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{c.title}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{c.body}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.65 }}>
            {t("aboutAbaCases.whyNote")}
          </p>
        </div>
      </section>

      {/* ── 5. The free ABA timesheet and payment tracker ────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.trackerTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.trackerP1Before")}{" "}
            <SeoLink to="free-aba-timesheet-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.trackerP1Link")}</SeoLink>{" "}
            {t("aboutAbaCases.trackerP1After")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.trackerP2")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t("aboutAbaCases.trackerP3Before")}{" "}
            <SeoLink to="aba-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.trackerP3LinkTimesheet")}</SeoLink>{t("aboutAbaCases.trackerP3Mid1")}{" "}
            <SeoLink to="rbt-timesheet" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.trackerP3LinkRbt")}</SeoLink>{" "}
            {t("aboutAbaCases.trackerP3Mid2")}{" "}
            <SeoLink to="aba-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.trackerP3LinkPay")}</SeoLink>{" "}
            {t("aboutAbaCases.trackerP3After")}
          </p>
        </div>
      </section>

      {/* ── 6. For agencies posting ABA cases ─────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.agenciesTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.agenciesP1Before")}{" "}
            <SeoLink to="aba-agencies" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.agenciesP1Link")}</SeoLink>{" "}
            {t("aboutAbaCases.agenciesP1After")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.agenciesP2")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t("aboutAbaCases.agenciesP3Before")}{" "}
            <SeoLink to="post-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.agenciesP3LinkPost")}</SeoLink>{" "}
            {t("aboutAbaCases.agenciesP3Mid1")}{" "}
            <SeoLink to="aba-agencies" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.agenciesP3LinkOverview")}</SeoLink>.{" "}
            {t("aboutAbaCases.agenciesP3Mid2")}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.agenciesP3LinkAlerts")}</SeoLink>{" "}
            {t("aboutAbaCases.agenciesP3After")}
          </p>
          <div style={{ marginTop: 22 }}>
            <button onClick={() => goAgencySignup("agencies_section")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: T.gradientIndigo, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: T.shadowIndigo }}>
              <BriefcaseIcon size={16} color="#fff" /> {agencyCtaLabel}
            </button>
            <AgencyPricingNote style={{ color: TEXT_MID, maxWidth: 620, marginTop: 12 }} />
          </div>
        </div>
      </section>

      {/* ── 7. Where ABAcases is expanding ───────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t("aboutAbaCases.expandTitle")}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t("aboutAbaCases.expandP1Before")}{" "}
            <SeoLink to="rbt-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.expandLinkFl")}</SeoLink>{t("aboutAbaCases.expandP1Mid1")}{" "}
            <SeoLink to="rbt-jobs-north-carolina" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.expandLinkNc")}</SeoLink>{t("aboutAbaCases.expandP1Mid2")}{" "}
            <SeoLink to="rbt-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t("aboutAbaCases.expandLinkCharlotte")}</SeoLink>.
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t("aboutAbaCases.expandP2")}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginTop: 14 }}>
            {t("aboutAbaCases.expandP3")}
          </p>
        </div>
      </section>

      {/* ── Founder ──────────────────────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: sectionPad }}>
        <FounderCard long isMobile={isMobile} onMessage={() => setContactOpen(true)} />
      </section>

      {contactOpen && <ContactModal onClose={() => setContactOpen(false)} />}
      <PublicFooter />
    </div>
  );
}
